import type {
  ActivityFeedItemDto,
  ExpenseResponseDto,
  GroupWithDashboard,
} from '@splitfiesta/shared';

import {
  EXAMPLE_SCENARIOS,
  type ExamplePayer,
  type ExampleScenario,
} from './exampleScenarios';

/**
 * The example group a brand-new user can open before they have invited
 * anyone or logged anything — so the payoff ("who owes whom") is visible
 * on day one. Tester feedback 2026-09-11: people finished onboarding and
 * had nothing to look at.
 *
 * Pure data, one source for web and mobile. Nothing here is ever sent to
 * the API or written to a cache: the id is a React key, the members are
 * not users, and the screens that render it take no QueryClient.
 *
 * Balances are COMPUTED from the expenses and the recorded payment
 * (core has no balance math of its own — the API does), so the numbers
 * stay self-consistent if someone edits an amount.
 */

/** Only ever a React key / route param. Never a real group id. */
export const EXAMPLE_GROUP_ID = 'example';

export interface ExampleMember {
  id: string;
  name: string;
}

export interface ExampleSuggestion {
  fromUserId: string;
  toUserId: string;
  amount: number;
}

export interface BuildExampleGroupOptions {
  /** The user's own currency, so the amounts read as theirs. */
  currency: string;
  /** The signed-in user — member #1, so the screen says "You are owed". */
  you: ExampleMember;
  /**
   * Resolves `exampleGroup.*` keys WITHOUT a namespace; each app binds
   * its own (`groups:exampleGroup.*` on both). `description` (two
   * friends) and `descriptionPair` (one) are templates and receive
   * `{ tagline, first, second? }`. Member names are proper nouns and need
   * no translation.
   */
  t: (key: string, params?: Record<string, string>) => string;
  /** Which of the five scenarios to build. Defaults to Barcelona. */
  scenario?: ExampleScenario;
  /** Injectable clock so "3 days ago" is testable. Defaults to the app
   *  session start so repeated builds are deep-equal (see SESSION_STARTED_AT). */
  now?: Date;
  /**
   * Cover photo, so the card looks like a real group rather than the
   * gradient fallback. Each app supplies its own: mobile a bundled asset
   * resolved to a URI, web a file under public/.
   */
  imageUrl?: string;
}

export interface ExampleGroup {
  /** The scenario it was built from; the sandbox shows that scenario's adds. */
  scenario: ExampleScenario['key'];
  group: GroupWithDashboard;
  expenses: ExpenseResponseDto[];
  settlement: ActivityFeedItemDto;
  suggestions: ExampleSuggestion[];
  members: ExampleMember[];
}

// Dates are anchored to the moment the app started, not to each call:
// the API client rebuilds the fixture on every request, and React Query's
// structural sharing only keeps a stable `data` reference when refetches
// are deep-equal. Per-call `new Date()` made every refetch "new", which
// re-seeded edit forms in a loop.
const SESSION_STARTED_AT = new Date();

const ANA: ExampleMember = { id: 'example-ana', name: 'Ana' };
const LEO: ExampleMember = { id: 'example-leo', name: 'Leo' };

interface Transfer {
  fromUserId: string;
  toUserId: string;
  amount: number;
}

/**
 * balance = paid − own share, then a recorded payment moves money from
 * payer to recipient (payer's balance rises, recipient's falls). Sums to
 * zero by construction.
 */
export function computeBalances(
  memberIds: string[],
  expenses: Pick<ExpenseResponseDto, 'payers' | 'splits'>[],
  settlements: Transfer[]
): Record<string, number> {
  const balance: Record<string, number> = Object.fromEntries(
    memberIds.map(id => [id, 0])
  );
  for (const expense of expenses) {
    for (const p of expense.payers) balance[p.userId] += p.amount;
    for (const s of expense.splits) balance[s.userId] -= s.amount;
  }
  for (const s of settlements) {
    balance[s.fromUserId] += s.amount;
    balance[s.toUserId] -= s.amount;
  }
  return balance;
}

/**
 * Fewest-payments settle-up: each debtor pays creditors largest-first
 * until square. Small and deterministic; the API's real optimiser is the
 * source of truth for real groups.
 */
export function suggestSettlements(
  balance: Record<string, number>
): ExampleSuggestion[] {
  const creditors = Object.entries(balance)
    .filter(([, b]) => b > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([id, b]) => ({ id, left: b }));
  const debtors = Object.entries(balance)
    .filter(([, b]) => b < 0)
    .sort((a, b) => a[1] - b[1])
    .map(([id, b]) => ({ id, left: -b }));
  const out: ExampleSuggestion[] = [];
  for (const d of debtors) {
    for (const c of creditors) {
      if (d.left === 0) break;
      if (c.left === 0) continue;
      const amount = Math.min(d.left, c.left);
      out.push({ fromUserId: d.id, toUserId: c.id, amount });
      d.left -= amount;
      c.left -= amount;
    }
  }
  return out;
}

// A full sentence per cast size rather than a joined list: Hermes has no
// Intl.ListFormat, and each language words "You and Ana" its own way.
function describeCast(
  t: BuildExampleGroupOptions['t'],
  tagline: string,
  friends: ExampleMember[]
): string {
  const [first, second] = friends.map(m => m.name);
  return second
    ? t('description', { tagline, first, second })
    : t('descriptionPair', { tagline, first });
}

export function buildExampleGroup({
  currency,
  you,
  t,
  now = SESSION_STARTED_AT,
  imageUrl,
  scenario = EXAMPLE_SCENARIOS[0],
}: BuildExampleGroupOptions): ExampleGroup {
  const CAST: Record<'ana' | 'leo', ExampleMember> = { ana: ANA, leo: LEO };
  const members: ExampleMember[] = [you, ...scenario.cast.map(c => CAST[c])];
  const byKey: Record<ExamplePayer, ExampleMember> = { you, ...CAST };
  const copy = (key: string) => t(`scenarios.${scenario.key}.${key}`);
  const EXPENSES = scenario.expenses;
  const SETTLEMENT = scenario.settlement;
  const iso = (daysAgo: number) =>
    new Date(now.getTime() - daysAgo * 86_400_000).toISOString();
  const day = (daysAgo: number) => iso(daysAgo).slice(0, 10);

  const expenses: ExpenseResponseDto[] = EXPENSES.map((e, i) => {
    const payer = byKey[e.paidBy];
    const share = e.amount / members.length;
    return {
      id: `example-expense-${i + 1}`,
      groupId: EXAMPLE_GROUP_ID,
      title: copy(`expenses.${e.key}`),
      amount: e.amount,
      currency,
      category: e.category,
      ...(e.subcategory ? { subcategory: e.subcategory } : {}),
      date: day(e.daysAgo),
      splitMethod: 'equal',
      createdBy: payer.id,
      createdByName: payer.name,
      createdAt: iso(e.daysAgo),
      updatedAt: iso(e.daysAgo),
      payers: [{ userId: payer.id, userName: payer.name, amount: e.amount }],
      splits: members.map(m => ({
        userId: m.id,
        userName: m.name,
        amount: share,
      })),
    };
  });

  const settlementTransfer: Transfer = {
    fromUserId: byKey[SETTLEMENT.from].id,
    toUserId: byKey[SETTLEMENT.to].id,
    amount: SETTLEMENT.amount,
  };
  const settlement: ActivityFeedItemDto = {
    id: 'example-settlement-1',
    type: 'settlement',
    user: byKey[SETTLEMENT.from].name,
    userId: settlementTransfer.fromUserId,
    description: copy('settlementNote'),
    amount: SETTLEMENT.amount,
    currency,
    groupName: copy('name'),
    groupId: EXAMPLE_GROUP_ID,
    timestamp: iso(SETTLEMENT.daysAgo),
    paidBy: [
      {
        userId: settlementTransfer.fromUserId,
        amount: SETTLEMENT.amount,
        userName: byKey[SETTLEMENT.from].name,
      },
    ],
    splits: [
      {
        userId: settlementTransfer.toUserId,
        amount: SETTLEMENT.amount,
        userName: byKey[SETTLEMENT.to].name,
      },
    ],
  };

  const balance = computeBalances(
    members.map(m => m.id),
    expenses,
    [settlementTransfer]
  );
  const suggestions = suggestSettlements(balance);
  const totalSpent = expenses.reduce((acc, e) => acc + e.amount, 0);
  const createdAt = iso(scenario.createdDaysAgo);

  const group: GroupWithDashboard = {
    id: EXAMPLE_GROUP_ID,
    name: copy('name'),
    description: describeCast(t, copy('tagline'), members.slice(1)),
    currency,
    category: scenario.category,
    ...(imageUrl ? { image: imageUrl, imageFocalY: 0.5 } : {}),
    isActive: true,
    simplifyDebts: true,
    defaultSplitMethod: 'equal',
    memberCount: members.length,
    expenseCount: expenses.length,
    lastActivityAt: iso(1),
    createdBy: you.id,
    createdAt,
    updatedAt: iso(1),
    userRole: 'owner',
    userBalance: balance[you.id],
    recentExpenses: expenses,
    balances: members.map(m => ({
      userId: m.id,
      userName: m.name,
      balance: balance[m.id],
      currency,
      role: m.id === you.id ? 'owner' : 'member',
      joinedAt: createdAt,
    })),
    dashboard: {
      totalSpent,
      avgPerMember: totalSpent / members.length,
      currency,
      pendingSettlementsToConfirm: 0,
    },
  };

  return {
    scenario: scenario.key,
    group,
    expenses,
    settlement,
    suggestions,
    members,
  };
}
